/**
 * AppShell — top-level layout for in-app authenticated routes.
 *
 * Layout:
 *  - `< md` (< 768px):     single column, no sidebar, mobile <BottomTabBar> at fixed bottom
 *                          (unless `noMobileTabs` is set, e.g. ExamMode immersive view).
 *  - `md` to `lg` (768–1023): grid-cols-[76px_1fr] — icon-only rail.
 *  - `lg+` (≥ 1024px):     grid-cols-[248px_1fr] — full rail with labels.
 *
 * The right column always renders a sticky <Topbar> followed by the canvas <main>
 * (centered, padded, with `id="main-content"` so the global skip-nav link works).
 *
 * Use this for every in-app authenticated page (Dashboard, Study*, Exam*, Profile,
 * Billing, Review, admin/*). Marketing/auth pages keep <PageShell> + <Header> + <Footer>.
 */
import type { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { BottomTabBar } from './BottomTabBar';
import { useUIStore } from '../../stores/uiStore';

export interface AppShellProps {
  children: ReactNode;
  /** Constrain canvas width. Defaults to `full` (the rail handles the perceived width). */
  maxWidth?: 'full' | '7xl';
  /** Reserved for a future compact density. Only `cozy` is implemented. */
  density?: 'cozy' | 'compact';
  /** Hide the mobile bottom tab bar — used by immersive flows like an active exam. */
  noMobileTabs?: boolean;
  /** Override the topbar entirely (e.g. for full-bleed flows). */
  topbar?: ReactNode;
  /** Hide the topbar entirely. */
  noTopbar?: boolean;
  /** Extra Tailwind classes for the `main` element. */
  mainClassName?: string;
}

const MAX_W: Record<NonNullable<AppShellProps['maxWidth']>, string> = {
  full: 'max-w-full',
  '7xl': 'max-w-7xl',
};

export function AppShell({
  children,
  maxWidth = 'full',
  noMobileTabs = false,
  topbar,
  noTopbar = false,
  mainClassName = '',
}: AppShellProps) {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);

  /* Grid template for the rail column. `md–lg` is always the 76px icon rail;
     at `lg+` the user's collapse preference decides between the 76px rail and
     the full 248px labelled rail. Collapsing hands the delta to the canvas. */
  const gridCols = sidebarCollapsed
    ? 'grid-cols-1 md:grid-cols-[76px_1fr]'
    : 'grid-cols-1 md:grid-cols-[76px_1fr] lg:grid-cols-[248px_1fr]';

  return (
    <div className={`min-h-screen bg-canvas text-slate-900 dark:bg-canvas-dark dark:text-slate-100 grid ${gridCols}`}>
      {/* Sidebar — hidden on mobile (replaced by BottomTabBar) */}
      <div className="hidden md:block md:sticky md:top-0 md:h-screen">
        <Sidebar />
      </div>

      {/* Main column */}
      <div className="flex min-h-screen min-w-0 flex-col">
        {!noTopbar && (topbar ?? <Topbar />)}
        <main
          id="main-content"
          tabIndex={-1}
          className={`mx-auto w-full flex-1 px-4 py-7 outline-none sm:px-6 lg:px-8 ${MAX_W[maxWidth]} ${noMobileTabs ? '' : 'pb-24 md:pb-7'} ${mainClassName}`}
        >
          {children}
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      {!noMobileTabs && <BottomTabBar />}
    </div>
  );
}
